// src/me/MyProfile.js
import React, { useEffect, useState } from "react";
import ContentLayout from "../layouts/ContentLayout";
import { useHistory } from "react-router-dom";
import { toPinyinSync, capitalizePinyin } from '../utils/pinyinUtil';
import { getUser } from "../utils/auth";
import * as wk from "../utils/wkCommon";
import QrPusat from "../components/QrPusat";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function absoluteUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return API_URL + url;
}

function normalize(item) {
  if (!item) return null;
  if (item.id && item.attributes) {
    return { id: item.id, ...item.attributes };
  }
  return { ...item };
}

async function safeFetch(url, opts = {}) {
  // prefer wk.apiFetch wrapper if available (project util)
  if (wk && typeof wk.apiFetch === "function") {
    try {
      return await wk.apiFetch(url, opts);
    } catch (e) {
      console.debug("wk.apiFetch failed, falling back to fetch:", e);
    }
  }

  const headers = { ...(opts.headers || {}) };

  // try multiple possible localStorage keys for JWT
  const jwt = localStorage.getItem("jwt");

  console.debug("safeFetch: url=", url, "jwtPresent=", !!jwt);

  if (jwt) {
    headers["Authorization"] = `Bearer ${jwt}`;
  }

  // per project rule: do not use credentials: "include"
  const resp = await fetch(url, { ...opts, headers });

  // try to read body text for helpful debugging
  let bodyText = "";
  try { bodyText = await resp.clone().text(); } catch (e) { bodyText = "<no body>"; }
  console.debug("safeFetch resp:", resp.status, resp.statusText, bodyText.slice(0, 300));

  if (resp.status === 401) {
    // clear common tokens to force a fresh login (helpful during debugging)
    console.warn("safeFetch: 401 Unauthorized — clearing stored tokens");
    localStorage.removeItem("jwt");
    throw new Error(`HTTP 401 Unauthorized - ${bodyText}`);
  }

  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status} ${resp.statusText} - ${bodyText}`);
  }

  // attempt to parse JSON (most Strapi responses)
  try {
    return await resp.json();
  } catch (e) {
    // fallback: return raw text
    return bodyText;
  }
}

/* ---------------- ProfileDetails ---------------- */
export function ProfileDetails() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [umat, setUmat] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) Prefer the canonical stored user object (from auth storage)
        const authUser = getUser(); // may be null
        console.debug("ProfileDetails: authUser from getUser()", authUser);

        // 2) Try various localStorage keys that previous code used
        const storedUmatJson = localStorage.getItem("umatData");
        let storedUmat = null;
        if (storedUmatJson) {
          try { storedUmat = JSON.parse(storedUmatJson); } catch (e) { storedUmat = storedUmatJson; }
        }

        // 3) Short-circuit: if authUser contains umat attributes (full object) use it
        if (authUser && authUser.umat) {
          // authUser.umat may be either a code or full object; accept it
          setUmat(typeof authUser.umat === "object" ? authUser.umat : { umatID: authUser.umat });
          setLoading(false);
          return;
        }

        // 4) If we found a storedUmat object (saved at login), use it
        if (storedUmat) {
          setUmat(storedUmat);
          setLoading(false);
          return;
        }

        // 5) Also accept legacy individual keys (just a code)
        const code = localStorage.getItem("umatID") || localStorage.getItem("kodeUmat") || localStorage.getItem("kode");
        if (code) {
          // we only have a kode — treat as not linked to full umat data and redirect
          console.debug("ProfileDetails: only umat code found, redirecting to LinkToUmat to complete linking:", code);
          history.push("/linktoprofile");
          return;
        }

        // 6) Nothing relevant found — redirect user to link page
        console.debug("ProfileDetails: no linked umat data found, redirecting to LinkToUmat");
        history.push("/linktoprofile");
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err.message || String(err));
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const avatar = (umat && wk.resolveUmatAvatarSync(umat, "avatarpic")) ||  (wk.getAvatarPlaceholder(umat?.gender) : null);

    const nametitle = wk.getUmatDisplayLabel(umat);
    const namesubtitle = wk.getUmatDisplayLabelLine2(umat);

  const chinese = (umat && (umat.chineseName + " " + capitalizePinyin(toPinyinSync(umat.chineseName)))) || "-";
  const pinyin = (umat && (umat.pinyinName)) || "";
  const displayName = (umat && (umat.displayName || umat.fullName)) || (umat && (umat.name || umat.fullname)) || (localStorage.getItem('displayName') || localStorage.getItem('fullName')) || "Unknown";
  const gender = (umat && (umat.gender)) || "-";
  const birthdate = (umat && (umat.birthdate)) || "-";
  const district = (umat &&  (umat.area ? umat.area.name : "")) || "-";
  const mobile = (umat && (umat.mobilePhone || umat.mobile)) || "-";
  const useremail = (umat && (umat.email)) || '-';
  const address = (umat && (umat.address)) || "-";
  const kodeUmat = (umat && (umat.umatID)) || (localStorage.getItem('umatID') || localStorage.getItem('kodeUmat') || localStorage.getItem('kode')) || "-";

  // build the main node JSX
  const mainNode = (
    <>
    <style>{`
    .profile-info {
      all: revert;              /* 🔥 reset semua pengaruh parent */
      font-size: inherit;
      color: inherit;
    }

    .profile-grid {
      display: grid !important;
      grid-template-columns: 9rem 1fr !important;
      gap: 0.5rem 0;
    }

    .profile-grid .label {
      font-weight: 600;
    }

    /* supaya text panjang tidak rusak */
    .profile-grid div {
      min-width: 0;
      word-break: break-word;
    }
    `}</style>
    <div className="-mx-4 md:-mx-12 px-4 md:px-12">
      {loading ? (
        <div>Loading profile...</div>
      ) : error ? (
        <div style={{ color: "crimson" }}>{error}</div>
      ) : (
        <div className="w-full bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="grid md:grid-cols-[240px_1fr]">
            {/* Kiri */}
            <div className="bg-gray-50 flex flex-col items-center justify-start p-8">
              <img
                src={avatar}
                alt="Avatar"
                className="w-40 h-40 object-cover border border-gray-300 shadow-sm"
                style={{ borderRadius: 8 }}
              />
            </div>

            {/* Kanan */}
            <div className="p-8 text-gray-800">
              <div className="mb-6">
                <div style={{ fontSize:"1.4rem", fontWeight:700, marginTop:"0.4rem" }}>{nametitle}</div>
                <div style={{ fontSize:"1.2rem", fontWeight:500, marginTop:"0.2rem", marginBottom:"1.2rem" }}>{namesubtitle}</div>
              </div>

              <div className="profile-info">
                <div className="profile-grid">
                  
                  <div className="label">Gender:</div>
                  <div>{gender}</div>

                  <div className="label">Usia:</div>
                  <div>
                    {birthdate
                      ? `${Math.floor((new Date() - new Date(birthdate)) / (365.25 * 24 * 60 * 60 * 1000))} tahun`
                      : "-"}
                  </div>

                  <div className="label">Area:</div>
                  <div>{district}</div>

                  <div className="label">Mobile:</div>
                  <div>{mobile}</div>

                  <div className="label">Email:</div>
                  <div className="break-words">{useremail}</div>

                  <div className="label">Alamat:</div>
                  <div className="break-words">{address || "-"}</div>

                  <div className="label">Kode Umat:</div>
                  <div className="text-blue-700 font-semibold">{kodeUmat}</div>

                </div>
              </div>
            </div>
          </div>
        </div>

      )}
    </div>
    </>
  );

  return (
    <ContentLayout
      title="My Profile"
      hideSearch={true}
      hideHeaderActions={true}
      singleColumn={false}
      mainNode={mainNode}
    />
  );
}

/* ---------------- QrLiEn ---------------- */
export function QrLiEn() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [umat, setUmat] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) Prefer the canonical stored user object (from auth storage)
        const authUser = getUser(); // may be null
        console.debug("ProfileDetails: authUser from getUser()", authUser);

        // 2) Try various localStorage keys that previous code used
        const storedUmatJson = localStorage.getItem("umatData") || localStorage.getItem("umat") || localStorage.getItem("userUmat");
        let storedUmat = null;
        if (storedUmatJson) {
          try { storedUmat = JSON.parse(storedUmatJson); } catch (e) { storedUmat = storedUmatJson; }
        }

        // 3) Short-circuit: if authUser contains umat attributes (full object) use it
        if (authUser && authUser.umat) {
          // authUser.umat may be either a code or full object; accept it
          setUmat(typeof authUser.umat === "object" ? authUser.umat : { umatID: authUser.umat });
          setLoading(false);
          return;
        }

        // 4) If we found a storedUmat object (saved at login), use it
        if (storedUmat) {
          setUmat(storedUmat);
          setLoading(false);
          return;
        }

        // 5) Also accept legacy individual keys (just a code)
        const code = localStorage.getItem("umatID") || localStorage.getItem("kodeUmat") || localStorage.getItem("kode");
        if (code) {
          // we only have a kode — treat as not linked to full umat data and redirect
          console.debug("ProfileDetails: only umat code found, redirecting to LinkToUmat to complete linking:", code);
          history.push("/linktoprofile");
          return;
        }

        // 6) Nothing relevant found — redirect user to link page
        console.debug("ProfileDetails: no linked umat data found, redirecting to LinkToUmat");
        history.push("/linktoprofile");
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err.message || String(err));
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const chinese = (umat && (umat.chineseName + " " + capitalizePinyin(toPinyinSync(umat.chineseName)))) || "-";
  const displayName = (umat && (umat.fullName || umat.nickName)) || "Unknown";
  const qrData = (umat && (umat.umatID)) || "-";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData)}`;

  const mainNode = (
    <div style={{ display: "flex", justifyContent: "center", padding: 18 }}>
      <div
        style={{
          width: "100%",
          maxWidth: 380,          // card max width on desktop
          boxSizing: "border-box",
          textAlign: "center",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          background: "#fff",
          padding: 12,
        }}
      >
        {/* top bar */}
        <div style={{
          background: "#083a8a",
          color: "#fff",
          padding: 14,
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
        }}>
          <div style={{ fontSize: 22 }}>禮恩佛院</div>
        </div>

        {/* middle white box - squared corners */}
        <div style={{
          background: "#fff",
          padding: 18,
          borderLeft: "12px solid #083a8a",
          borderRight: "12px solid #083a8a",
          boxSizing: "border-box",
        }}>
          {/* responsive QR image */}
          <div style={{ width: "100%", maxWidth: 280, margin: "0 auto" }}>
            <img
              src={qrUrl}
              alt="qr-li-en"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>

          <div style={{ marginTop: 12, fontWeight: 700, color: "#083a8a", fontSize: 18 }}>{qrData}</div>
        </div>

        {/* bottom bar */}
        <div style={{
          background: "#083a8a",
          color: "#fff",
          padding: 14,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{chinese}</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>{displayName}</div>
        </div>
      </div>
    </div>
  );

  return (
    <ContentLayout
      title="QR Li En"
      hideSearch={true}
      hideHeaderActions={true}
      singleColumn={false}
      mainNode={mainNode}
    />
  );
}

/* ---------------- QrPusat ---------------- */
export function QrPusatPage() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [umat, setUmat] = useState(null);
  const [error, setError] = useState(null);
  const authUser = getUser();


  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // 1) Prefer the canonical stored user object (from auth storage)
        const authUser = getUser(); // may be null
        console.debug("ProfileDetails: authUser from getUser()", authUser);

        // 2) Try various localStorage keys that previous code used
        const storedUmatJson = localStorage.getItem("umatData") || localStorage.getItem("umat") || localStorage.getItem("userUmat");
        let storedUmat = null;
        if (storedUmatJson) {
          try { storedUmat = JSON.parse(storedUmatJson); } catch (e) { storedUmat = storedUmatJson; }
        }

        // 3) Short-circuit: if authUser contains umat attributes (full object) use it
        if (authUser && authUser.umat) {
          // authUser.umat may be either a code or full object; accept it
          setUmat(typeof authUser.umat === "object" ? authUser.umat : { umatID: authUser.umat });
          setLoading(false);
          return;
        }

        // 4) If we found a storedUmat object (saved at login), use it
        if (storedUmat) {
          setUmat(storedUmat);
          setLoading(false);
          return;
        }

        // 5) Also accept legacy individual keys (just a code)
        const code = localStorage.getItem("umatID") || localStorage.getItem("kodeUmat") || localStorage.getItem("kode");
        if (code) {
          // we only have a kode — treat as not linked to full umat data and redirect
          console.debug("ProfileDetails: only umat code found, redirecting to LinkToUmat to complete linking:", code);
          history.push("/linktoprofile");
          return;
        }

        // 6) Nothing relevant found — redirect user to link page
        console.debug("ProfileDetails: no linked umat data found, redirecting to LinkToUmat");
        history.push("/linktoprofile");
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err.message || String(err));
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

/*
  const chinese = (umat && (umat.chineseName + " " + capitalizePinyin(toPinyinSync(umat.chineseName)))) || "-";
  const displayName = (umat && (umat.fullName || umat.nickName)) || "Unknown";
  const qrData = (umat && (umat.tsfyUmatID)) || "-";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=360x360&data=${encodeURIComponent(qrData)}`;

  const mainNode = (
    <div style={{ display: "flex", justifyContent: "center", padding: 18 }}>
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          boxSizing: "border-box",
          textAlign: "center",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          background: "#fff",
          padding: 12,
        }}
      >
        <div style={{ background: "#0e6c63", color: "#fff", padding: 16, borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
          <div style={{ fontSize: 24 }}>天聖佛院</div>
        </div>

        <div style={{
          background: "#fff",
          padding: 18,
          borderLeft: "12px solid #0e6c63",
          borderRight: "12px solid #0e6c63",
          boxSizing: "border-box",
        }}>
          <div style={{ width: "100%", maxWidth: 360, margin: "0 auto" }}>
            <img src={qrUrl} alt="qr-pusat" style={{ width: "100%", height: "auto", display: "block" }} />
          </div>

          <div style={{ marginTop: 12, fontSize: 20, color: "#0e6c63", fontWeight: 600 }}>{qrData}</div>
        </div>

        <div style={{ background: "#0e6c63", color: "#fff", padding: 14, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{chinese}</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>{displayName}</div>
        </div>
      </div>
    </div>
  );
*/
  return (
    <ContentLayout
      title="QR Pusat"
      hideSearch={true}
      hideHeaderActions={true}
      singleColumn={false}
      mainNode={
        <QrPusat umat={authUser.umat} />
      }
    />
  );
}

export default { ProfileDetails, QrLiEn, QrPusatPage };
